import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import FilterDropdown from '../TeamPortalUtil/FilterDropdown';
import { getAlertTypeClass } from '../AlertHub/AlertHub';

// Leaflet imports
import {
  MapContainer,
  TileLayer,
  ScaleControl,
  useMap,
  Marker,
  Popup,
  GeoJSON
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import "react-leaflet-markercluster/dist/styles.min.css";
import { ImageOverlay } from "react-leaflet"; // add to your react-leaflet imports

// Icons
import {
  LuRefreshCw,
  LuLayers,
  LuContainer,
  LuZap,
  LuTriangle,
  LuActivity,
  LuClock,
  LuChevronDown,
  LuChevronRight,
} from 'react-icons/lu';

import { HiMiniGlobeAmericas } from "react-icons/hi2";
import { BsList } from "react-icons/bs";
import { MdOutlineInventory, MdOutlineEmail, MdDynamicFeed, MdOutlineError } from "react-icons/md";
import { GoNumber } from "react-icons/go";
import { TbAlertSquareRounded, TbRadar2 } from "react-icons/tb";
import { IoInformationCircle } from "react-icons/io5";
import { TiWarning } from "react-icons/ti";

// CSS
import './PipelineStatus.css';

// Additional Components
import TeamHeader from '../TeamHeader/TeamHeader';
// import exampleData from './example.json';

// Email data fetch
import { usePrefectHistory } from "../AITab/prefect";

// GIS Alerts
interface Alerts {
  id: number;
  category: string;
  title: string;
  body: string;
  type: string;
  created_at: any;
  api_key_name: string;
  owner: number;
  archive: boolean;
}

// Interface for getting the available org content
interface InventoryItems {
  partID: number;
  partName: string;
  partPicture?: any;
  currentAmount: number;
  unitCost: number;
  category: string;
  partDocument?: any;
  partLink?: string;
  lastCounted?: string | null; // ISO date string
  location?: string | null;
  description?: string | null;
  mpn?: string | null;
  manufacturer?: string | null;
  location_picture?: any;
  stock_target?: number | null;
  sold_in_packets?: boolean;
  amount_per_packet?: number | null;
}

// Forecast
interface InventoryForecast {
  id: number;
  part: number;
  expectedAmount: number;
  cost: number;
  orderedDate: Date | null;
  arrivalDate: Date | null;
  status: string;
  sourceLinks: string;
  notes: string;
  requestor: string;
  requestor_email?: string;
  priority?: string;
  lead_time?: string | null;
  is_packet?: boolean;
  number_of_packets?: number | null;
  amount_per_packet?: number | null;
  pack_cost?: number;
  expectedArrivalDate?: Date | null;
  alert_generated?: boolean;
  belongs_to_batch: number | null;
  belongs_to_batch_name?: string | null;
}

// Organization
interface MarketingOrg {
  id: number;
  ORG_NAME: string;
  ORG_LOGO: string;
  ORG_PLAN: string;
}

interface SwarmService {
  service: string;
  status: string;
  replicas: string;
  image: string;
  last_event_time: string;
  last_error: string;
  last_log: string;
  mem_limit?: number;
}

interface ContainerMetric {
  service: string;
  status: string;
  state: string;
  cpu_pct: number;
  mem_usage_mb: number;
  mem_limit_mb?: number;
  mem_pct?: number;
  last_log: string;
  container_id: string;
  image: string;
}

interface Meta {
  served_in_ms: number;
  strategy: string;
  stale_sections: string[];
  all_fresh: boolean;
  warning: string | null;
  last_pulls: {
    [key: string]: string; // ISO timestamp
  };
}

interface GeoJsonLast5 {
  ltg_lon: number;
  ltg_lat: number;
  idx: number;
  polarity: number;
  dttime_utc: string;
  hrl: number;
  precip24h: number;
  erc: number;
  kbdi: number;
  relh_min: number;
  relh_max: number;
  bi?: number;
  vpd?: number;
  fm100?: number;
  fm1000?: number;
  lcc?: number | null;
}

interface GeoJsonStats {
  count: number;
  negative: number;
  positive: number;
  avg_kbdi: number | null;
  avg_erc: number | null;
  avg_hrl: number | null;
  avg_precip24h: number | null;
  avg_relh_min: number | null;
  avg_relh_max: number | null;
}

interface GeoJsonFeed {
  id: string;
  area: string;
  time: string;
  path: string;
  filename: string;
  name: string | null;
  total_features?: number;
  last5?: GeoJsonLast5[];
  stats?: GeoJsonStats;
  error?: string;
  expected?: string;
}

interface MrmsPoint {
  lat: number;
  lng: number;
  t: number;   // temperature °F
  p: number;   // precipitation in
}

interface MrmsPayload {
  points: MrmsPoint[];
  timestamps: {
    precip: string;
    temp: string;
  };
  meta: {
    temp_unit: string;
    precip_unit: string;
    bounds: [number, number, number, number]; // [minLat, maxLat, minLng, maxLng]
  };
}

interface MrmsData {
  payload: MrmsPayload;
  last_updated: string;
}

interface Region {
  id: number;
  fnn_area: string;
  format: string;
  note: string | null;
  last_change: string;
  shapefile_path: string;
}

interface StatusData {
  swarm: SwarmService[];
  heartbeat: {
    table_name: string;
    health_score: string;
    last_lcc_detected: string;
    last_classified_stroke: string;
    last_grouping_op: string;
    updated_at: string;
  };
  solutions: any[];
  containers: ContainerMetric[];
  _meta: Meta;
}

const fmt = (iso: string) =>
  new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });

const fmtTime = (iso: string) =>
  new Date(iso).toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });

const fmtTimeUTC = (iso: string) => {
  const d = new Date(iso);
  const raw = d.getUTCHours();
  const ampm = raw >= 12 ? 'PM' : 'AM';
  const hh = String(raw % 12 || 12).padStart(2, '0');
  const mm = String(d.getUTCMinutes()).padStart(2, '0');
  const ss = String(d.getUTCSeconds()).padStart(2, '0');
  return `${hh}:${mm}:${ss} ${ampm} UTC`;
};

const AREA_COLORS: Record<string, string> = {};        // for geojson feeds

const PALETTE = [
  '#28aee1', // accent blue
  '#f59e0b', // amber
  '#10b981', // emerald
  '#8b5cf6', // violet
  '#ef4444', // red
  '#f97316', // orange
  '#06b6d4', // cyan
  '#ec4899', // pink
];

const TIME_COLORS: Record<string, string> = {
  daily: '#ffffff',     // high contrast highlight (clean + readable on all colors)
  weekly: '#859396',    // near-black (strong structural anchor)
  monthly: '#514d47',   // amber (intentional semantic warmth, not neutral)
  historical: '#111827' // cyan (cool archival/older data feel)
};

const SOLUTION_TYPE_COLOR_MAP: Record<number, string> = {
  0: '#8b5cf6', // violet
  1: '#10b981', // emerald
  2: '#ef4444', // red
  5: '#06b6d4', // cyan
};

const makeAreaIcon = (fill: string, stroke?: string) =>
  new (require('leaflet').DivIcon)({
    className: '',
    html: `
      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18">
        <circle 
          cx="9" 
          cy="9" 
          r="7" 
          fill="${fill}" 
          fill-opacity="0.85" 
          stroke="${stroke}" 
          stroke-width="2"
        />
      </svg>
    `,
    iconSize: [18, 18],
    iconAnchor: [9, 9],
  });

// Inner component — lives inside MapContainer so it can access the map instance
const FlyToRegion = ({ center, zoom }: { center: [number, number]; zoom: number }) => {
  const map = useMap();
  useEffect(() => {
    map.flyTo(center, zoom, { duration: 1.2 });
  }, [center[0], center[1], zoom]);
  return null;
};

// ── MRMS color scales ────────────────────────────────────────────
// Temperature scale (°F): blue (cold) → green → yellow → red (hot)
const getTempColor = (t: number): [number, number, number, number] => {
  // Clamp to a sensible range for surface temp display
  const stops: Array<[number, [number, number, number]]> = [
    [32, [0, 0, 255]],
    [50, [0, 100, 255]],
    [60, [0, 200, 200]],
    [70, [0, 220, 0]],
    [80, [255, 220, 0]],
    [90, [255, 100, 0]],
    [100, [255, 0, 0]],
    [110, [180, 0, 100]],
  ];
  if (t <= stops[0][0]) return [...stops[0][1], 200] as [number, number, number, number];
  if (t >= stops[stops.length - 1][0]) return [...stops[stops.length - 1][1], 200] as [number, number, number, number];
  for (let i = 0; i < stops.length - 1; i++) {
    const [t0, c0] = stops[i];
    const [t1, c1] = stops[i + 1];
    if (t >= t0 && t <= t1) {
      const f = (t - t0) / (t1 - t0);
      return [
        Math.round(c0[0] + f * (c1[0] - c0[0])),
        Math.round(c0[1] + f * (c1[1] - c0[1])),
        Math.round(c0[2] + f * (c1[2] - c0[2])),
        200,
      ];
    }
  }
  return [128, 128, 128, 200];
};

// Precipitation scale (inches/hr): transparent (0) → light blue → blue → green → yellow → red → purple
const getPrecipColor = (p: number): [number, number, number, number] => {
  if (p <= 0) return [0, 0, 0, 0]; // fully transparent for no precip
  const stops: Array<[number, [number, number, number, number]]> = [
    [0.01, [100, 180, 255, 160]],
    [0.1, [0, 140, 255, 200]],
    [0.25, [0, 220, 0, 220]],
    [0.5, [0, 160, 0, 230]],
    [1.0, [255, 220, 0, 240]],
    [2.0, [255, 120, 0, 245]],
    [4.0, [255, 0, 0, 255]],
    [8.0, [180, 0, 180, 255]],
  ];
  if (p >= stops[stops.length - 1][0]) return stops[stops.length - 1][1];
  for (let i = 0; i < stops.length - 1; i++) {
    const [p0, c0] = stops[i];
    const [p1, c1] = stops[i + 1];
    if (p >= p0 && p <= p1) {
      const f = (p - p0) / (p1 - p0);
      return [
        Math.round(c0[0] + f * (c1[0] - c0[0])),
        Math.round(c0[1] + f * (c1[1] - c0[1])),
        Math.round(c0[2] + f * (c1[2] - c0[2])),
        Math.round(c0[3] + f * (c1[3] - c0[3])),
      ];
    }
  }
  return [100, 180, 255, 160];
};

// ── MRMS Canvas Layer ────────────────────────────────────────────
// Renders points into an offscreen canvas, then drapes it over the
// map as a geo-referenced ImageOverlay — smooth, projection-correct,
// no boxy edge artefacts.
const MrmsCanvasLayer: React.FC<{
  points: MrmsPoint[];
  layer: 'temp' | 'precip';
  bounds: [number, number, number, number]; // [minLat, maxLat, minLng, maxLng]
}> = ({ points, layer, bounds }) => {

  const [imageUrl, setImageUrl] = useState<string | null>(null);

  useEffect(() => {
    if (points.length === 0) return;

    const [minLat, maxLat, minLng, maxLng] = bounds;

    // Resolution: ~0.01° grid spacing — use enough canvas pixels for crisp output
    // but not so many that it's slow. 4px per 0.01° grid cell is a good balance.
    const PX_PER_CELL = 4;
    const GRID_DEG = 0.01;

    const lngSpan = maxLng - minLng;
    const latSpan = maxLat - minLat;

    const canvasW = Math.round((lngSpan / GRID_DEG) * PX_PER_CELL);
    const canvasH = Math.round((latSpan / GRID_DEG) * PX_PER_CELL);

    const canvas = document.createElement('canvas');
    canvas.width = canvasW;
    canvas.height = canvasH;

    const ctx = canvas.getContext('2d')!;
    ctx.clearRect(0, 0, canvasW, canvasH);

    // Use ImageData for fast per-pixel writes
    const imgData = ctx.createImageData(canvasW, canvasH);
    const buf = imgData.data; // Uint8ClampedArray, RGBA

    // Gaussian-style soft kernel radius (in canvas pixels) for smooth blending
    const KERNEL_R = Math.round(PX_PER_CELL * 1.5);
    const KERNEL_R2 = KERNEL_R * KERNEL_R;

    for (const pt of points) {
      const [r, g, b, a] = layer === 'temp'
        ? getTempColor(pt.t)
        : getPrecipColor(pt.p);

      if (a === 0) continue;

      // Map lat/lng → canvas pixel (lat is flipped: top = maxLat)
      const cx = Math.round(((pt.lng - minLng) / lngSpan) * canvasW);
      const cy = Math.round(((maxLat - pt.lat) / latSpan) * canvasH);

      // Paint a soft disc rather than a hard rectangle
      for (let dy = -KERNEL_R; dy <= KERNEL_R; dy++) {
        for (let dx = -KERNEL_R; dx <= KERNEL_R; dx++) {
          const dist2 = dx * dx + dy * dy;
          if (dist2 > KERNEL_R2) continue;

          const px = cx + dx;
          const py = cy + dy;
          if (px < 0 || px >= canvasW || py < 0 || py >= canvasH) continue;

          // Gaussian falloff: full opacity at centre, fades to ~0 at edge
          const falloff = Math.exp(-dist2 / (KERNEL_R2 * 0.45));
          const alpha = Math.round(a * falloff);

          const idx = (py * canvasW + px) * 4;

          // Alpha-blend onto existing pixel so overlapping cells blend smoothly
          const existingA = buf[idx + 3];
          if (existingA === 0) {
            buf[idx] = r;
            buf[idx + 1] = g;
            buf[idx + 2] = b;
            buf[idx + 3] = alpha;
          } else {
            // Porter-Duff "over" blend
            const srcA = alpha / 255;
            const dstA = existingA / 255;
            const outA = srcA + dstA * (1 - srcA);
            if (outA > 0) {
              buf[idx] = Math.round((r * srcA + buf[idx] * dstA * (1 - srcA)) / outA);
              buf[idx + 1] = Math.round((g * srcA + buf[idx + 1] * dstA * (1 - srcA)) / outA);
              buf[idx + 2] = Math.round((b * srcA + buf[idx + 2] * dstA * (1 - srcA)) / outA);
              buf[idx + 3] = Math.round(outA * 255);
            }
          }
        }
      }
    }

    ctx.putImageData(imgData, 0, 0);

    // Optional: apply a light Gaussian blur pass via CSS filter on the canvas
    // (the ImageOverlay will render it smoothly scaled by Leaflet anyway)
    setImageUrl(canvas.toDataURL('image/png'));
  }, [points, layer, bounds]);

  if (!imageUrl) return null;

  const leafletBounds: [[number, number], [number, number]] = [
    [bounds[0], bounds[2]], // SW: [minLat, minLng]
    [bounds[1], bounds[3]], // NE: [maxLat, maxLng]
  ];

  return (
    <ImageOverlay
      url={imageUrl}
      bounds={leafletBounds}
      opacity={0.82}
      zIndex={400}
    />
  );
};

// Color scale legend for MRMS
const MrmsLegend: React.FC<{ layer: 'temp' | 'precip' }> = ({ layer }) => {
  const tempStops = [32, 50, 60, 70, 80, 90, 100, 110];
  const precipStops = [0.01, 0.1, 0.25, 0.5, 1.0, 2.0, 4.0, 8.0];

  const stops = layer === 'temp' ? tempStops : precipStops;
  const label = layer === 'temp' ? '°F' : 'in/hr';
  const getColor = layer === 'temp' ? getTempColor : getPrecipColor;

  return (
    <div style={{
      background: 'rgba(255,255,255,0.92)',
      borderRadius: 6,
      padding: '8px 12px',
      fontSize: 11,
      boxShadow: '0 1px 5px rgba(0,0,0,.2)',
      minWidth: 80,
    }}>
      <div style={{ fontWeight: 600, marginBottom: 5 }}>
        {layer === 'temp' ? 'Temp' : 'Precip'} ({label})
      </div>
      {stops.map((val) => {
        const [r, g, b] = getColor(val);
        return (
          <div key={val} style={{ display: 'flex', alignItems: 'center', marginBottom: 3 }}>
            <span style={{
              width: 12, height: 12,
              borderRadius: 2,
              background: `rgb(${r},${g},${b})`,
              marginRight: 6,
              flexShrink: 0,
              border: '1px solid rgba(0,0,0,0.1)',
            }} />
            <span style={{ color: '#374151' }}>{val}</span>
          </div>
        );
      })}
    </div>
  );
};


const InventoryCountStatus = ({
  items,
  forecastItems,
  lateExpanded,
  setLateExpanded,
}: {
  items: InventoryItems[];
  forecastItems: InventoryForecast[];
  lateExpanded: boolean;
  setLateExpanded: React.Dispatch<React.SetStateAction<boolean>>;
}) => {

  const now = new Date();
  now.setHours(0, 0, 0, 0); // compare date only, ignore time

  // ── Count brackets (existing) ──────────────────────────────
  const neverCounted = items.filter(i => !i.lastCounted);

  const overSixty = items.filter(i => {
    if (!i.lastCounted) return false;
    const days = Math.floor((now.getTime() - new Date(i.lastCounted).getTime()) / (1000 * 60 * 60 * 24));
    return days > 60;
  });

  const overThirty = items.filter(i => {
    if (!i.lastCounted) return false;
    const days = Math.floor((now.getTime() - new Date(i.lastCounted).getTime()) / (1000 * 60 * 60 * 24));
    return days > 30 && days < 60;
  });

  const total = items.length || 1;

  const brackets = [
    { label: 'Never Counted', count: neverCounted.length, colorClass: 'inv-bar-never', badgeClass: 'inv-badge-never' },
    { label: '30–59 Days Ago', count: overThirty.length, colorClass: 'inv-bar-orange', badgeClass: 'inv-badge-orange' },
    { label: '60+ Days Ago', count: overSixty.length, colorClass: 'inv-bar-red', badgeClass: 'inv-badge-red' },
  ];

  // ── Late orders (new) ──────────────────────────────────────
  const lateOrders = forecastItems.filter(f => {
    const ordered = ['ordered', 'in transit', 'pending', 'processing'];
    if (!ordered.includes(f.status?.toLowerCase())) return false;

    // prefer expectedArrivalDate, fall back to arrivalDate
    const arrival = f.expectedArrivalDate ?? f.arrivalDate;
    if (!arrival) return false;

    const arrivalDay = new Date(arrival);
    arrivalDay.setHours(0, 0, 0, 0);
    return arrivalDay < now;
  });

  const daysLate = (f: InventoryForecast): number => {
    const arrival = f.expectedArrivalDate ?? f.arrivalDate;
    if (!arrival) return 0;
    const arrivalDay = new Date(arrival);
    arrivalDay.setHours(0, 0, 0, 0);
    return Math.floor((now.getTime() - arrivalDay.getTime()) / (1000 * 60 * 60 * 24));
  };

  return (
    <div className="inv-status-wrap">

      {/* ── Count Header ── */}
      <div className="inv-late-header inv-late-header-top">
        <GoNumber size={15} className="inv-late-icon" style={{ color: 'var(--pipe-sub)' }} />
        <span className="inv-late-title">Items in need of count</span>
        <span className="inv-late-count-pill inv-late-count-pill-neutral">
          {neverCounted.length + overThirty.length + overSixty.length}
        </span>
      </div>

      {/* ── Stacked bar ── */}
      <div className="inv-bar-track">
        {brackets.map(b => (
          <div
            key={b.label}
            className={`inv-bar-segment ${b.colorClass}`}
            style={{ width: `${(b.count / total) * 100}%` }}
            title={`${b.label}: ${b.count}`}
          />
        ))}
      </div>

      {/* ── Bracket cards ── */}
      <div className="inv-bracket-grid">
        {brackets.map(b => (
          <div key={b.label} className={`inv-bracket-card ${b.badgeClass}`}>
            <div className="inv-bracket-top">
              <span className={`inv-bracket-pip ${b.colorClass}`} />
              <span className="inv-bracket-label">{b.label}</span>
            </div>
            <div className="inv-bracket-count">{b.count}</div>
            <div className="inv-bracket-sub">
              {`${((b.count / total) * 100).toFixed(1)}% of inventory`}
            </div>
          </div>
        ))}
      </div>

      {items.length === 0 && (
        <p className="inv-empty">No inventory data loaded.</p>
      )}

      {/* ── Late Orders ─────────────────────────────────────── */}
      <div
        className="inv-late-header inv-late-header-clickable"
        onClick={() => setLateExpanded(prev => !prev)}
      >
        <LuTriangle size={13} className="inv-late-icon" />
        <span className="inv-late-title">Late Orders</span>
        <span className="inv-late-count-pill">{lateOrders.length}</span>
        {lateOrders.length > 0 && (
          lateExpanded
            ? <LuChevronDown size={14} color="var(--pipe-sub)" />
            : <LuChevronRight size={14} color="var(--pipe-sub)" />
        )}
      </div>

      {lateExpanded && (
        lateOrders.length === 0 ? (
          <p className="inv-empty inv-empty-late">All orders are on time.</p>
        ) : (
          <div className="inv-late-list">
            {lateOrders
              .sort((a, b) => daysLate(b) - daysLate(a))
              .map(f => {
                const late = daysLate(f);
                const severity = late >= 14 ? 'inv-late-severe' : late >= 7 ? 'inv-late-warn' : 'inv-late-mild';
                const arrival = f.expectedArrivalDate ?? f.arrivalDate;

                return (
                  <div key={f.id} className={`inv-late-row ${severity}`}>
                    <div className="inv-late-row-left">
                      <span className="inv-late-part">
                        {items.find(i => i.partID === f.part)?.partName ?? `Part #${f.part}`}
                      </span>
                      {f.belongs_to_batch_name && (
                        <span className="inv-late-batch">{f.belongs_to_batch_name}</span>
                      )}
                    </div>
                    <div className="inv-late-row-mid">
                      <span className="inv-late-status">Priority</span>
                      {f.priority && (
                        <span className={`inv-late-priority inv-priority-${f.priority.toLowerCase()}`}>
                          {f.priority}
                        </span>
                      )}
                    </div>
                    <div className="inv-late-row-right">
                      <span className="inv-late-days">{late}d late</span>
                      <span className="inv-late-date">
                        Due {new Date(arrival!).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                      </span>
                    </div>
                  </div>
                );
              })}
          </div>
        )
      )}

    </div>
  );
};

const PipelineStatus = () => {

  const [searchParams] = useSearchParams();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [expandedService, setExpandedService] = useState<string | null>(null);
  const [lateExpanded, setLateExpanded] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const [data, setData] = useState<StatusData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'map' | 'table'>('map');
  const [lastFetched, setLastFetched] = useState<Date | null>(null);
  const [now, setNow] = useState(Date.now());
  const [activeAreas, setActiveAreas] = useState<Set<string>>(new Set());
  const [activeTimes, setActiveTimes] = useState<Set<string>>(new Set(['daily', 'weekly', 'monthly', 'historical']));


  useEffect(() => {
    const fetchAvailableTables = async () => {
      setTablesLoading(true);
      try {
        const res = await fetch('/pipelinemonitoring/availabletables/', { // http://localhost:3000/tables.json
          credentials: 'same-origin',
        });
        if (!res.ok) return;
        const json = await res.json();
        const tables: string[] = json.tables ?? [];
        setAvailableTables(tables);
        // If the default table isn't in the list, fall back to the first available
        if (tables.length > 0 && !tables.includes(tableName)) {
          setTableName(tables[0]);
        }
      } catch (err) {
        console.error('❌ Error fetching available tables:', err);
      } finally {
        setTablesLoading(false);
      }
    };

    fetchAvailableTables();
  }, []); // run once on mount

  // ── MRMS Variables ──────────────────────────────
  const [mrmsData, setMrmsData] = useState<MrmsData | null>(null);
  const [mrmsLoading, setMrmsLoading] = useState(false);
  const [mrmsLayer, setMrmsLayer] = useState<'temp' | 'precip'>('precip');

  const fetchMrms = async () => {
    setMrmsLoading(true);
    try {
      const res = await fetch('/pipelinemonitoring/mrms/', // http://localhost:3000/mrms.json
        { credentials: "same-origin" });
      if (!res.ok) throw new Error(res.statusText);
      const json = await res.json();
      setMrmsData(json);
    } catch (err) {
      console.error('❌ Error fetching MRMS:', err);
    } finally {
      setMrmsLoading(false);
    }
  };

  useEffect(() => {
    fetchMrms();
    const interval = setInterval(fetchMrms, 60_000);
    return () => clearInterval(interval);
  }, []);

  // Staleness: > 10 minutes since last_updated
  const mrmsStaleness = useMemo(() => {
    if (!mrmsData?.last_updated) return true;
    return (Date.now() - new Date(mrmsData.last_updated).getTime()) > 10 * 60 * 1000;
  }, [mrmsData?.last_updated, now]);

  const mrmsMapCenter: [number, number] = mrmsData
    ? [
      (mrmsData.payload.meta.bounds[0] + mrmsData.payload.meta.bounds[1]) / 2,
      (mrmsData.payload.meta.bounds[2] + mrmsData.payload.meta.bounds[3]) / 2,
    ]
    : [28, -83.5];

  // ── Region / Solutions filter ──────────────────────────────
  const [regions, setRegions] = useState<Region[]>([]);
  const [selectedRegion, setSelectedRegion] = useState<string | null>(null);
  const [regionSolutionsLoading, setRegionSolutionsLoading] = useState(false);
  const mapRef = useRef<any>(null);
  const markerRefs = useRef<Record<number, any>>({});

  const [regionData, setRegionData] = useState<{
    region: string;
    table: string;          // ← add this
    geoJson: any | null;
    solutions: any[];
  } | null>(null);

  const formatTimeAgo = (date: Date | null) => {
    if (!date) return "—";

    const rawDiff = (now - date.getTime()) / 1000;

    const diff = Math.max(
      0,
      Math.floor((Date.now() - date.getTime()) / 1000)
    );


    if (diff < 60) return `${diff} seconds ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)} minutes ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} hours ago`;

    return `${Math.floor(diff / 86400)} days ago`;
  };

  const getColorForType = (type: number) => {
    return SOLUTION_TYPE_COLOR_MAP[type] || '#9ca3af'; // fallback (gray)
  };

  // Functions to open the main slips
  const [openSlips, setOpenSlips] = useState<Record<string, boolean>>({
    server: false,
    ai: false,
    inventory: false,
    gis: false,
  });

  const toggleSlip = (key: string) => {
    setOpenSlips((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const toggleArea = (area: string) =>
    setActiveAreas(prev => {
      const next = new Set(prev);
      next.has(area) ? next.delete(area) : next.add(area);
      return next;
    });

  const toggleTime = (time: string) =>
    setActiveTimes(prev => {
      const next = new Set(prev);
      next.has(time) ? next.delete(time) : next.add(time);
      return next;
    });

  // ---------------------------------------- 
  // GIS Alerts START
  // ---------------------------------------- 

  const [gisAlerts, setGisAlerts] = useState<Alerts[]>([]);
  const [gisPage, setGisPage] = useState(1);
  const [gisHasMore, setGisHasMore] = useState(true);
  const [gisLoading, setGisLoading] = useState(false);
  const [gisLoadingMore, setGisLoadingMore] = useState(false);

  const fetchGISAlerts = async (pageNumber: number) => {
    const params = new URLSearchParams({
      department: "GIS",
      archive: "false",
      page: String(pageNumber),
      page_size: "9",
    });

    if (pageNumber === 1) {
      setGisLoading(true);

      try {
        const res = await fetch(`/api/alerts/?${params.toString()}`, {
          credentials: "same-origin",
        });

        if (!res.ok) throw new Error("Failed to fetch GIS alerts");

        const data = await res.json();
        const results: Alerts[] = data.results ?? data;

        setGisAlerts(results);
        setGisHasMore(!!data.next);
        setGisPage(1);
      } catch (err) {
        console.error(err);
      } finally {
        setGisLoading(false);
      }
    } else {
      setGisLoadingMore(true);

      try {
        const res = await fetch(`/api/alerts/?${params.toString()}`, {
          credentials: "same-origin",
        });

        if (!res.ok) throw new Error("Failed to fetch GIS alerts");

        const data = await res.json();
        const results: Alerts[] = data.results ?? data;

        setGisAlerts(prev => [...prev, ...results]);
        setGisHasMore(!!data.next);
      } catch (err) {
        console.error(err);
      } finally {
        setGisLoadingMore(false);
      }
    }
  };

  useEffect(() => {
    if (!openSlips.gis) return;

    fetchGISAlerts(1);
  }, [openSlips.gis]);

  const handleGISLoadMore = () => {
    if (!gisHasMore || gisLoadingMore) return;

    const next = gisPage + 1;
    setGisPage(next);
    fetchGISAlerts(next);
  };


  // ---------------------------------------- 
  // GIS ALERTS END
  // ---------------------------------------- 


  // With this:
  const defaultTable =
    searchParams.get('table') || 'apache_solutions.usa_se_solutions';

  const [tableName, setTableName] = useState<string>(defaultTable);
  const [availableTables, setAvailableTables] = useState<string[]>([]);
  const [tablesLoading, setTablesLoading] = useState(false);

  const fetchStatus = async () => {
    setRefreshing(true);
    try {
      const response = await fetch(
        `/pipelinemonitoring/status/?table=${tableName}`, // http://localhost:3000/${tableName}.json
        { credentials: 'same-origin' }
      );
      if (!response.ok) throw new Error(`Error: ${response.statusText}`);

      const jsonData = await response.json();
      setData(jsonData);
      setGeoJsonFeeds(jsonData.geojson_feeds ?? []);
      setError(null);
      setLastFetched(new Date());
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 30000);
    return () => clearInterval(interval);
  }, [tableName]);

  useEffect(() => {
    const interval = setInterval(() => {
      setNow(Date.now());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // -------------------------------
  // Inventory Section
  // -------------------------------

  const [invInfo, setInvInfo] = useState<InventoryItems[]>([]);
  const [forecastInfo, setForecastInfo] = useState<InventoryForecast[]>([]);

  const fetchInvApiInfo = async () => {
    try {
      const response = await fetch('/api/inventory_view/', {
        credentials: "same-origin" // ensures cookies/session are sent for auth
      });

      const data = await response.json();
      // console.log(data)

      if (response.ok) {
        setInvInfo(data); // Assign the fetched gallery data to state
      } else {
        console.error("Failed to load api information:", data.error || response.statusText);
      }
    } catch (error) {
      console.error("❌ Error fetching inventory info:", error);
    }
  };

  useEffect(() => {
    fetchInvApiInfo();
  }, []);

  const fetchForecastInfo = async () => {
    try {
      const response = await fetch('/api/inventory_forecast/', {
        credentials: "same-origin" // ensures cookies/session are sent for auth
      });

      const data = await response.json();
      // console.log(data)

      if (response.ok) {
        setForecastInfo(data); // Assign the fetched gallery data to state
      } else {
        console.error("Failed to load api information:", data.error || response.statusText);
      }
    } catch (error) {
      console.error("❌ Error fetching forecast info:", error);
    }
  };

  useEffect(() => {
    fetchForecastInfo();
  }, []);

  // Fetch available regions once on mount
  const fetchRegions = async () => {
    try {
      const res = await fetch('/pipelinemonitoring/regions/', { // http://localhost:3000/regions.json
        credentials: 'same-origin',
      });
      if (!res.ok) return;
      const json = await res.json();
      setRegions(json.regions ?? []);
    } catch (err) {
      console.error('❌ Error fetching regions:', err);
    }
  };

  useEffect(() => {
    fetchRegions();
  }, []);

  // When selectedRegion changes, fetch its GeoJSON and solutions
  useEffect(() => {
    if (!selectedRegion) {
      setRegionData(null);
      return;
    }

    const controller = new AbortController();
    const { signal } = controller;
    const regionAtFetchTime = selectedRegion; // capture for closure
    const tableAtFetchTime = tableName;

    const fetchRegionData = async () => {
      setRegionSolutionsLoading(true);
      try {
        const [geoRes, solRes] = await Promise.all([
          fetch(
            `/pipelinemonitoring/regions/geojson/?area=${encodeURIComponent(selectedRegion)}`, // /http://localhost:3000/${encodeURIComponent(regionAtFetchTime)}.json
            { credentials: 'same-origin', signal }
          ),
          fetch(
            `/pipelinemonitoring/solutions/?table=${tableName}&region=${encodeURIComponent(selectedRegion)}`, //`http://localhost:3000/${encodeURIComponent(regionAtFetchTime)}_solutions.json`
            { credentials: 'same-origin', signal }
          ),
        ]);

        if (signal.aborted) return;

        const geoJson = geoRes.ok ? (await geoRes.json()) : null;
        const solJson = solRes.ok ? (await solRes.json()) : null;

        if (signal.aborted) return;

        // Commit both together atomically, tagged with the region they belong to
        setRegionData({
          region: regionAtFetchTime,
          table: tableAtFetchTime,               // ← tag
          geoJson: geoJson ? (geoJson.geojson ?? geoJson) : null,
          solutions: solJson?.data ?? [],
        });

      } catch (err: any) {
        if (err.name !== 'AbortError') {
          console.error('❌ Error fetching region data:', err);
        }
      } finally {
        if (!signal.aborted) setRegionSolutionsLoading(false);
      }
    };

    fetchRegionData();
    return () => controller.abort();

  }, [selectedRegion, tableName]);

  // ---------------------------------------- 
  // Geojson feed start
  // ---------------------------------------- 
  const [geoJsonFeeds, setGeoJsonFeeds] = useState<GeoJsonFeed[]>([]);
  const [geoViewMode, setGeoViewMode] = useState<'map' | 'table'>('map');
  const [expandedFeedId, setExpandedFeedId] = useState<string | null>(null);


  geoJsonFeeds.forEach((feed) => {
    if (!AREA_COLORS[feed.area]) {
      const idx = Object.keys(AREA_COLORS).length % PALETTE.length;
      AREA_COLORS[feed.area] = PALETTE[idx];
    }
  });

  // Sync activeAreas when new feed areas come in
  useEffect(() => {
    const areas = new Set(geoJsonFeeds.map(f => f.area));
    setActiveAreas(prev => {
      // Add any new areas as active by default, keep existing toggle state
      const next = new Set(prev);
      areas.forEach(a => { if (!next.has(a)) next.add(a); });
      return next;
    });
  }, [geoJsonFeeds]);

  interface AreaLegendProps {
    areaColors: Record<string, string>;
    timeColors: Record<string, string>;
    activeAreas: Set<string>;
    activeTimes: Set<string>;
    onToggleArea: (area: string) => void;
    onToggleTime: (time: string) => void;
  }

  const AreaLegend: React.FC<AreaLegendProps> = ({
    areaColors,
    timeColors,
    activeAreas,
    activeTimes,
    onToggleArea,
    onToggleTime,
  }) => (
    <div style={{
      background: 'rgba(255,255,255,0.95)',
      borderRadius: 8,
      padding: '10px 12px',
      fontSize: 12,
      boxShadow: '0 2px 8px rgba(0,0,0,.18)',
      minWidth: 130,
      userSelect: 'none',
    }}>

      {/* Areas */}
      <div style={{ fontWeight: 700, marginBottom: 6, fontSize: 11, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#6b7280' }}>
        Area
      </div>
      {Object.entries(areaColors).map(([area, color]) => {
        const active = activeAreas.has(area);
        return (
          <div
            key={area}
            onClick={() => onToggleArea(area)}
            title={active ? `Hide ${area}` : `Show ${area}`}
            style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: 5,
              cursor: 'pointer',
              opacity: active ? 1 : 0.35,
              transition: 'opacity 0.15s',
              borderRadius: 4,
              padding: '2px 4px',
              margin: '0 -4px 4px',
            }}
            onMouseEnter={e => (e.currentTarget.style.background = '#f3f4f6')}
            onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
          >
            <span style={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              background: active ? color : '#d1d5db',
              marginRight: 7,
              flexShrink: 0,
              transition: 'background 0.15s',
            }} />
            <span style={{ color: active ? '#111827' : '#9ca3af', fontSize: 11.5, transition: 'color 0.15s' }}>
              {area}
            </span>
            <span style={{ marginLeft: 'auto', fontSize: 10, color: '#d1d5db', paddingLeft: 6 }}>
              {active ? '●' : '○'}
            </span>
          </div>
        );
      })}

      {/* Divider */}
      <div style={{ height: 1, background: '#e5e7eb', margin: '8px 0' }} />

      {/* Time */}
      <div style={{ fontWeight: 700, marginBottom: 6, fontSize: 11, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#6b7280' }}>
        Timeline
      </div>
      {Object.entries(timeColors).map(([time, color]) => {
        const active = activeTimes.has(time);
        return (
          <div
            key={time}
            onClick={() => onToggleTime(time)}
            title={active ? `Hide ${time}` : `Show ${time}`}
            style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: 5,
              cursor: 'pointer',
              opacity: active ? 1 : 0.35,
              transition: 'opacity 0.15s',
              borderRadius: 4,
              padding: '2px 4px',
              margin: '0 -4px 4px',
            }}
            onMouseEnter={e => (e.currentTarget.style.background = '#f3f4f6')}
            onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
          >
            <span style={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              background: active ? '#d1d5db' : '#f3f4f6',
              border: `2.5px solid ${active ? color : '#e5e7eb'}`,
              marginRight: 7,
              flexShrink: 0,
              transition: 'border-color 0.15s',
            }} />
            <span style={{ color: active ? '#111827' : '#9ca3af', fontSize: 11.5, textTransform: 'capitalize', transition: 'color 0.15s' }}>
              {time}
            </span>
            <span style={{ marginLeft: 'auto', fontSize: 10, color: '#d1d5db', paddingLeft: 6 }}>
              {active ? '●' : '○'}
            </span>
          </div>
        );
      })}

      {/* "All / None" quick actions */}
      <div style={{ height: 1, background: '#e5e7eb', margin: '8px 0 6px' }} />
      <div style={{ display: 'flex', gap: 6 }}>
        <button
          onClick={() => {
            Object.keys(timeColors).forEach(t => activeTimes.add(t));
            // force re-render via onToggle with no actual change — easier to just
            // call onToggle on the first inactive one, but the cleanest way is:
            Object.keys(timeColors).forEach(t => {
              if (!activeTimes.has(t)) onToggleTime(t);
            });
          }}
          style={{
            flex: 1, fontSize: 10, padding: '3px 0',
            border: '1px solid #e5e7eb', borderRadius: 4,
            background: 'none', cursor: 'pointer', color: '#6b7280',
          }}
          onMouseEnter={e => (e.currentTarget.style.background = '#f3f4f6')}
          onMouseLeave={e => (e.currentTarget.style.background = 'none')}
        >
          All
        </button>
        <button
          onClick={() => {
            Object.keys(timeColors).forEach(t => {
              if (activeTimes.has(t)) onToggleTime(t);
            });
          }}
          style={{
            flex: 1, fontSize: 10, padding: '3px 0',
            border: '1px solid #e5e7eb', borderRadius: 4,
            background: 'none', cursor: 'pointer', color: '#6b7280',
          }}
          onMouseEnter={e => (e.currentTarget.style.background = '#f3f4f6')}
          onMouseLeave={e => (e.currentTarget.style.background = 'none')}
        >
          None
        </button>
      </div>
    </div>
  );


  // ---------------------------------------- 
  // Geojson feed end
  // ---------------------------------------- 


  // ---------------------------------------- 
  // AI email history start
  // ---------------------------------------- 
  const { emailHistory } = usePrefectHistory();
  const [orgs, setOrgs] = useState<MarketingOrg[]>([]);
  const [expandedEmailId, setExpandedEmailId] = useState<string | null>(null);

  const todayEmails = emailHistory.filter((email) => {
    const now = new Date();

    const startOfTodayUTC = new Date(Date.UTC(
      now.getUTCFullYear(),
      now.getUTCMonth(),
      now.getUTCDate(),
      0, 0, 0, 0
    ));

    const endOfTodayUTC = new Date(Date.UTC(
      now.getUTCFullYear(),
      now.getUTCMonth(),
      now.getUTCDate(),
      23, 59, 59, 999
    ));

    const emailDate = new Date(email.email_timestamp);

    return emailDate >= startOfTodayUTC && emailDate <= endOfTodayUTC;
  });

  const fetchOrgs = async () => {
    try {
      const response = await fetch("/portal/api/marketing_orgs/", {
        credentials: "same-origin",
      });
      const data = await response.json();
      if (response.ok) {
        setOrgs(data);
      } else {
        console.error("Failed to load orgs:", data.error || response.statusText);
      }
    } catch (error) {
      console.error("❌ Error fetching orgs:", error);
    }
  };

  useEffect(() => {
    fetchOrgs();
  }, []);

  const getOrgName = (orgId: number) => {
    return orgs.find(o => o.id === orgId)?.ORG_NAME || `Org #${orgId}`;
  };


  // ---------------------------------------- 
  // AI email history end
  // ---------------------------------------- 

  interface SolutionLegendProps {
    typeColors: Record<number, string>;
  }

  const SolutionLegend: React.FC<SolutionLegendProps> = ({ typeColors }) => (
    <div
      style={{
        background: 'rgba(255,255,255,0.88)',
        borderRadius: 6,
        padding: '8px 12px',
        fontSize: 12,
        boxShadow: '0 1px 5px rgba(0,0,0,.2)',
      }}
    >
      {/* Title */}
      <div style={{ fontWeight: 600, marginBottom: 6 }}>
        Types
      </div>

      {Object.entries(typeColors).map(([type, color]) => (
        <div
          key={type}
          style={{
            display: 'flex',
            alignItems: 'center',
            marginBottom: 4,
          }}
        >
          <span
            style={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              background: color,
              marginRight: 6,
            }}
          />
          Type {type}
        </div>
      ))}
    </div>
  );

  const sortedContainers = useMemo(() => {
    if (!data?.containers) return [];
    return [...data.containers].sort((a, b) => b.cpu_pct - a.cpu_pct);
  }, [data?.containers]);

  // Use region-filtered solutions when a region is selected, otherwise fall back to status data
  const regionDataFresh =
    selectedRegion &&
    regionData?.region === selectedRegion &&
    regionData?.table === tableName;         // ← also check table

  const displayedSolutions: any[] =
    regionDataFresh ? regionData!.solutions : (data?.solutions ?? []);

  const regionGeoJson =
    regionDataFresh ? regionData!.geoJson : null;

  const solutionsMapCenter: [number, number] = displayedSolutions[0]
    ? [displayedSolutions[0].ltg_lat, displayedSolutions[0].ltg_lon]
    : [27.8, -81.5];

  const toggleExpand = (id: string) =>
    setExpandedId((prev) => (prev === id ? null : id));

  const toggleService = (name: string) =>
    setExpandedService((prev) => (prev === name ? null : name));

  // Stale or Live indicator based on META
  const isSectionStale = (section: string) =>
    data?._meta?.stale_sections?.includes(section);

  const getLastPullTime = (section: string) => {
    const iso = data?._meta?.last_pulls?.[section];
    return iso ? fmtTimeUTC(iso) : 'Unknown';
  };

  const solutionsStale = isSectionStale('solutions');
  const heartbeatStale = isSectionStale('heartbeat');
  const swarmStale = isSectionStale('swarm');
  const containersStale = isSectionStale('containers');
  const geojson_feedsStale = isSectionStale('geojson_feeds');

  if (loading && !data)
    return (
      <div className="notification-hub-wrapper">
        <TeamHeader title="Pipeline Status" />
        <div className="pipeline-loading">
          <LuRefreshCw className="spinning" size={16} /> Loading system status…
        </div>
      </div>
    );

  if (error)
    return (
      <div className="notification-hub-wrapper">
        <TeamHeader title="Pipeline Status" />
        <div className="pipeline-error">
          <LuTriangle size={16} /> {error}
        </div>
      </div>
    );

  if (!data) return null;


  return (
    <div className="customer-directory-body toa-body">
      <TeamHeader title="Pipeline Status" />
      <div className="pipeline-body">

        {/* ── Page Header ─────────────────────────────────────── */}
        <div className="pipeline-page-header">

          <h1 className="pipeline-page-title">Pipeline</h1>

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <button className="pipeline-refresh-btn" onClick={fetchStatus}>
              <LuRefreshCw size={13} className={refreshing ? 'spinning' : ''} />
              Refresh
            </button>

            <div className='pipeline-last-updated-text'>
              <p><strong>Last Updated</strong></p>
              <p>{formatTimeAgo(lastFetched)}</p>
            </div>
          </div>

        </div>

        {/* ── Server Section ─────────────────────────────────────── */}
        <div
          className={`pipeline-slip-header-closed slip-header ${openSlips.server ? 'open pipeline-slip-header' : ''
            }`}
          onClick={() => toggleSlip('server')}
        >
          Server
          <div className='slip-header-right'>
            <span className="slip-count-badge">3</span>
            <span className="slip-header-arrow">
              {openSlips.server ? (
                <LuChevronDown size={18} color="var(--pipe-sub)" />
              ) : (
                <LuChevronRight size={18} color="var(--pipe-sub)" />
              )}
            </span>
          </div>
        </div>

        {openSlips.server && (
          <div className="slip-content pipeline-slip-content">
            {/* ── Server First Row: Heartbeat + Solutions ──────────────────── */}
            <div className="pipeline-top-row">

              {/* Recent Solutions */}
              <section className="pipeline-section">
                <div className="pipeline-section-header">

                  <div className='pipeline-section-title-div'>
                    <LuZap size={14} />
                    <span className="pipeline-section-title">Recent Solutions</span>
                  </div>

                  <div className='pipeline-section-title-div'>

                    {/* Region and Table filter */}
                    <FilterDropdown
                      dropdownClassName="pipeline-filter-left-drop"
                      sideClass="detector-side-drop"
                      initialSelected={{
                        table: [tableName],
                        region: [],
                      }}
                      resetValue={{
                        table: [tableName],
                        region: [],
                      }}
                      sections={[
                        {
                          key: 'table',
                          title: 'Table',
                          options: availableTables.length > 0
                            ? availableTables.map((t) => t.split('.').pop()!)
                            : [tableName.split('.').pop()!],
                          type: 'radio',
                        },
                        {
                          key: 'region',
                          title: 'Region',
                          options: regions.map((r) => r.fnn_area),
                          type: 'radio',
                        },
                      ]}
                      onChange={(filters) => {
                        // Table: map the short display name back to the full qualified name
                        const pickedShort = filters.table?.[0];
                        if (pickedShort) {
                          const full = availableTables.find(
                            (t) => t.split('.').pop() === pickedShort
                          );
                          if (full && full !== tableName) setTableName(full);
                        }

                        // Region
                        const pickedRegion = filters.region?.[0] ?? null;
                        setSelectedRegion(pickedRegion);
                      }}
                    />

                    {/* Recent Solution Health Check */}
                    <span
                      className={`pipeline-health-badge ${solutionsStale ? 'degraded' : ''}`}
                      title={`Last run: ${getLastPullTime('solutions')}`}
                    >
                      <span className="pipeline-health-dot" />
                      {solutionsStale ? 'STALE' : 'LIVE'}
                    </span>

                    <div className="pipeline-section-table-toggle">
                      <HiMiniGlobeAmericas
                        className={viewMode === 'map' ? 'active' : ''}
                        onClick={() => setViewMode('map')}
                      />
                      <BsList
                        className={viewMode === 'table' ? 'active' : ''}
                        onClick={() => setViewMode('table')}
                      />
                    </div>
                  </div>

                </div>

                {viewMode === 'map' ? (
                  <div className="pipeline-section-body pipeline-section-body-ext pipeline-map">
                    {regionSolutionsLoading && (
                      <div className="pipeline-map-skeleton-overlay">
                        <div className="pipeline-map-skeleton-shimmer" />
                      </div>
                    )}
                    <MapContainer
                      center={solutionsMapCenter}
                      zoom={6}
                      minZoom={2}
                      ref={mapRef}
                      style={{ width: '100%', height: '100%' }}
                    >
                      <TileLayer
                        url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                      />
                      <ScaleControl position="bottomleft" metric={true} imperial={false} maxWidth={200} />

                      {/* Fly to region center when selectedRegion changes */}
                      {selectedRegion && (
                        <FlyToRegion center={solutionsMapCenter} zoom={7} />
                      )}

                      {/* Region GeoJSON overlay */}
                      {regionGeoJson && (
                        <GeoJSON
                          key={selectedRegion}
                          data={regionGeoJson}
                          style={{
                            fillColor: '#28aee1',
                            fillOpacity: 0.15,
                            color: '#28aee1',
                            weight: 2,
                            opacity: 0.8,
                            dashArray: '4',
                          }}
                        />
                      )}

                      <div
                        style={{
                          position: 'absolute',
                          bottom: 25,
                          right: 10,
                          zIndex: 1000,
                        }}
                      >
                        <SolutionLegend typeColors={SOLUTION_TYPE_COLOR_MAP} />
                      </div>

                      {displayedSolutions.map((sol: any) => (
                        <Marker
                          key={sol.id}
                          position={[sol.ltg_lat, sol.ltg_lon]}
                          icon={makeAreaIcon(getColorForType(sol.type))}
                          ref={(ref) => {
                            if (ref) markerRefs.current[sol.id] = ref;
                          }}
                        >
                          <Popup className="det-popup-wrapper" minWidth={260}>
                            <div className="det-popup">
                              <div className="det-popup-header">
                                <span className="det-popup-dot" style={{ backgroundColor: getColorForType(sol.type) }} />
                                <span className="det-popup-name">Solution #{sol.id}</span>
                              </div>
                              <div className="det-popup-row">
                                <span className="det-popup-label">Coordinates</span>
                                <span className="det-popup-value">
                                  {sol.ltg_lat.toFixed(4)}, {sol.ltg_lon.toFixed(4)}
                                </span>
                              </div>
                              <div className="det-popup-row">
                                <span className="det-popup-label">Type</span>
                                <span className="det-popup-value">{sol.type}</span>
                              </div>
                              <div className="det-popup-row">
                                <span className="det-popup-label">Polarity</span>
                                <span className="det-popup-value">
                                  {sol.polarity > 0 ? '+' : ''}{sol.polarity.toLocaleString()}
                                </span>
                              </div>
                              <div className="det-popup-row">
                                <span className="det-popup-label">Time</span>
                                <span className="det-popup-value">{fmt(sol.dttime_utc)}</span>
                              </div>
                              <div className="det-popup-row">
                                <span className="det-popup-label">Created</span>
                                <span className="det-popup-value">{fmt(sol.created_at)}</span>
                              </div>
                            </div>
                          </Popup>
                        </Marker>
                      ))}
                    </MapContainer>
                  </div>

                ) : (

                  <div className="pipeline-section-body pipeline-section-body-ext">
                    <div className="pipeline-solutions-list">
                      {displayedSolutions.map((sol: any) => (
                        <div key={sol.id} className="pipeline-solution-row">
                          <span className="pipeline-solution-id">#{sol.id}</span>
                          <div className="pipeline-solution-coords">
                            {sol.ltg_lat.toFixed(4)}, {sol.ltg_lon.toFixed(4)}
                            <br />
                            <span>
                              <span style={{ color: getColorForType(sol.type) }}>Type {sol.type}</span> &nbsp;·&nbsp; Polarity{' '}
                              {sol.polarity > 0 ? '+' : ''}
                              {sol.polarity.toLocaleString()}
                            </span>
                          </div>
                          <span className="pipeline-solution-time">
                            <LuClock size={11} style={{ marginRight: 3, verticalAlign: 'middle' }} />
                            {fmtTime(sol.dttime_utc)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                )}
              </section>

              {/* Heartbeat */}
              <section className="pipeline-section">
                <div className="pipeline-section-header">

                  <div className='pipeline-section-title-div'>
                    <LuActivity size={14} />
                    <span className="pipeline-section-title">Heartbeat</span>
                  </div>

                  {/* Heartbeat Health Check */}
                  <span className={`pipeline-health-badge ${heartbeatStale ? 'degraded' : ''}`}
                    title={`Last run: ${getLastPullTime('heartbeat')}`}>
                    <span className="pipeline-health-dot" />
                    {heartbeatStale ? 'STALE' : 'LIVE'}
                  </span>
                </div>

                <div className="pipeline-section-body pipeline-section-body-ext">
                  <div className="pipeline-heartbeat-grid">
                    <div className="pipeline-heartbeat-cell pipeline-table-name-cell">
                      <div className="pipeline-heartbeat-label">Monitoring Table</div>
                      <div className="pipeline-heartbeat-value">{data.heartbeat.table_name}</div>
                    </div>
                    <div className="pipeline-heartbeat-cell">
                      <div className="pipeline-heartbeat-label">Last LCC Detection</div>
                      <div className="pipeline-heartbeat-value">{fmt(data.heartbeat.last_lcc_detected)}</div>
                    </div>
                    <div className="pipeline-heartbeat-cell">
                      <div className="pipeline-heartbeat-label">Last Classified Stroke</div>
                      <div className="pipeline-heartbeat-value">{fmt(data.heartbeat.last_classified_stroke)}</div>
                    </div>
                    <div className="pipeline-heartbeat-cell">
                      <div className="pipeline-heartbeat-label">Last Grouping Op</div>
                      <div className="pipeline-heartbeat-value">{fmt(data.heartbeat.last_grouping_op)}</div>
                    </div>
                    <div className="pipeline-heartbeat-cell">
                      <div className="pipeline-heartbeat-label">Updated At</div>
                      <div className="pipeline-heartbeat-value">{fmt(data.heartbeat.updated_at)}</div>
                    </div>
                  </div>
                </div>
              </section>

            </div>

            {/* ── Server Second Row: Swarm ──────────────────── */}
            <div className="pipeline-single-row">
              {/* Swarm Services */}
              <section className="pipeline-section">
                <div className="pipeline-section-header">
                  <div className='pipeline-section-title-div'>
                    <LuLayers size={14} />
                    <span className="pipeline-section-title">Swarm Services</span>
                  </div>

                  {/* Swarm Health Check */}
                  <span className={`pipeline-health-badge ${swarmStale ? 'degraded' : ''}`}
                    title={`Last run: ${getLastPullTime('swarm')}`}>
                    <span className="pipeline-health-dot" />
                    {swarmStale ? 'STALE' : 'LIVE'}
                  </span>
                </div>

                <div className="pipeline-section-body" style={{ padding: '0 1.25rem' }}>
                  <div className="pipeline-swarm-table-wrap">
                    <table className="pipeline-swarm-table">
                      <thead>
                        <tr>
                          <th>Service</th>
                          <th>Status</th>
                          <th>Replicas</th>
                          <th>Image</th>
                          <th style={{ width: 24 }} />
                        </tr>
                      </thead>
                      <tbody>
                        {data.swarm.map((s: SwarmService, i: number) => {
                          const isExpanded = expandedService === s.service;
                          const isDegraded = s.status === 'DEGRADED';
                          const hasError =
                            s.last_error && s.last_error !== 'None';

                          return (
                            <React.Fragment key={i}>
                              <tr
                                className={[
                                  'pipeline-row-clickable',
                                  isDegraded ? 'pipeline-row-degraded' : '',
                                  isExpanded ? 'pipeline-row-active' : '',
                                ].join(' ')}
                                onClick={() => toggleService(s.service)}
                              >
                                <td style={{ fontWeight: 500, fontSize: '0.8rem' }}>
                                  {s.service}
                                </td>
                                <td>
                                  <span
                                    className={`pipeline-status-pill ${s.status.toLowerCase()}`}
                                  >
                                    <span
                                      className={`pipeline-status-dot ${isDegraded ? '' : 'pulsing'
                                        }`}
                                    />
                                    {s.status}
                                  </span>
                                </td>
                                <td>
                                  <span className="pipeline-replicas-tag">{s.replicas}</span>
                                </td>
                                <td>
                                  <span className="pipeline-image-tag">
                                    {s.image.split('/').pop()}
                                  </span>
                                </td>
                                <td>
                                  {isExpanded ? (
                                    <LuChevronDown size={14} color="var(--pipe-sub)" />
                                  ) : (
                                    <LuChevronRight size={14} color="var(--pipe-sub)" />
                                  )}
                                </td>
                              </tr>

                              {isExpanded && (
                                <tr className="pipeline-expand-row">
                                  <td colSpan={5}>
                                    <div className="pipeline-expand-content">
                                      <div className="pipeline-expand-item">
                                        <strong>Last Event</strong>
                                        {fmt(s.last_event_time)}
                                      </div>
                                      <div className="pipeline-expand-item">
                                        <strong>Full Image</strong>
                                        <code>{s.image}</code>
                                      </div>
                                      <div
                                        className={`pipeline-error-box ${hasError ? '' : 'no-error'
                                          }`}
                                      >
                                        {hasError
                                          ? s.last_error
                                          : 'No errors reported for this service.'}
                                      </div>

                                      <div className="pipeline-log-preview">
                                        <pre>{s.last_log || 'No logs available.'}</pre>
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </section>
            </div>
          </div>
        )}

        {/* ── AI Section ─────────────────────────────────────── */}
        <div
          className={`pipeline-slip-header-closed slip-header ${openSlips.ai ? 'open pipeline-slip-header' : ''
            }`}
          onClick={() => toggleSlip('ai')}
        >
          AI
          <div className='slip-header-right'>
            <span className="slip-count-badge">4</span>
            <span className="slip-header-arrow">
              {openSlips.ai ? (
                <LuChevronDown size={18} color="var(--pipe-sub)" />
              ) : (
                <LuChevronRight size={18} color="var(--pipe-sub)" />
              )}
            </span>
          </div>
        </div>

        {openSlips.ai && (
          <div className="slip-content pipeline-slip-content">
            {/* ── AI First Row: Geojson feeds + Emails──────────────────── */}
            <div className="pipeline-top-row">

              {/* GeoJson Feeds */}
              <section className="pipeline-section">
                <div className="pipeline-section-header">
                  <div className="pipeline-section-title-div">
                    <MdDynamicFeed size={16} />
                    <span className="pipeline-section-title">GeoJSON Feeds</span>
                  </div>

                  <div className="pipeline-section-title-div">
                    {/* Heartbeat Health Check */}
                    <span className={`pipeline-health-badge ${geojson_feedsStale ? 'degraded' : ''}`}
                      title={`Last run: ${getLastPullTime('heartbeat')}`}>
                      <span className="pipeline-health-dot" />
                      {geojson_feedsStale ? 'STALE' : 'LIVE'}
                    </span>


                    <div className="pipeline-section-table-toggle">
                      <HiMiniGlobeAmericas
                        className={geoViewMode === 'map' ? 'active' : ''}
                        onClick={() => setGeoViewMode('map')}
                      />
                      <BsList
                        className={geoViewMode === 'table' ? 'active' : ''}
                        onClick={() => setGeoViewMode('table')}
                      />
                    </div>
                  </div>
                </div>

                {/* ── Map View ── */}
                {geoViewMode === 'map' ? (
                  <div className="pipeline-section-body pipeline-section-body-ext pipeline-map">
                    <MapContainer
                      center={[35, -95]}
                      zoom={4}
                      minZoom={2}
                      style={{ width: '100%', height: '100%' }}
                    >
                      <TileLayer
                        url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                      />
                      <ScaleControl position="bottomleft" metric={true} imperial={false} maxWidth={200} />

                      {geoJsonFeeds
                        .filter(feed => activeAreas.has(feed.area) && activeTimes.has(feed.time))
                        .filter((feed) => feed.last5 && feed.last5.length > 0)
                        .flatMap((feed) =>
                          (feed.last5 ?? []).map((pt, ptIdx) => {
                            const fillColor = AREA_COLORS[feed.area] ?? '#28aee1';
                            const strokeColor = TIME_COLORS[feed.time] ?? '#ffffff';

                            const icon = makeAreaIcon(fillColor, strokeColor);

                            return (
                              <Marker
                                key={`${feed.id}-${ptIdx}`}
                                position={[pt.ltg_lat, pt.ltg_lon]}
                                icon={icon}
                              >
                                <Popup className="det-popup-wrapper" minWidth={240}>
                                  <div className="det-popup">

                                    <div className="det-popup-header">
                                      <span
                                        className="det-popup-dot"
                                        style={{
                                          background: fillColor,
                                          border: `2px solid ${strokeColor}`,
                                        }}
                                      />
                                      <span className="det-popup-name">
                                        {feed.area} · {feed.time}
                                      </span>
                                    </div>

                                    <div className="det-popup-row">
                                      <span className="det-popup-label">Coordinates</span>
                                      <span className="det-popup-value">
                                        {pt.ltg_lat.toFixed(4)}, {pt.ltg_lon.toFixed(4)}
                                      </span>
                                    </div>

                                    <div className="det-popup-row">
                                      <span className="det-popup-label">Time (UTC)</span>
                                      <span className="det-popup-value">{pt.dttime_utc}</span>
                                    </div>

                                    <div className="det-popup-row">
                                      <span className="det-popup-label">Polarity</span>
                                      <span className="det-popup-value">
                                        {pt.polarity > 0 ? '+' : ''}
                                        {pt.polarity.toLocaleString()}
                                      </span>
                                    </div>

                                    <div className="det-popup-row">
                                      <span className="det-popup-label">KBDI</span>
                                      <span className="det-popup-value">{pt.kbdi}</span>
                                    </div>

                                    <div className="det-popup-row">
                                      <span className="det-popup-label">ERC</span>
                                      <span className="det-popup-value">{pt.erc}</span>
                                    </div>

                                    <div className="det-popup-row">
                                      <span className="det-popup-label">HRL</span>
                                      <span className="det-popup-value">{pt.hrl}</span>
                                    </div>

                                    <div className="det-popup-row">
                                      <span className="det-popup-label">Precip 24h</span>
                                      <span className="det-popup-value">{pt.precip24h}</span>
                                    </div>

                                  </div>
                                </Popup>
                              </Marker>
                            );
                          })
                        )}

                      <div
                        style={{
                          position: 'absolute',
                          bottom: 25,
                          right: 10,
                          zIndex: 1000,
                        }}
                      >
                        <AreaLegend
                          areaColors={AREA_COLORS}
                          timeColors={TIME_COLORS}
                          activeAreas={activeAreas}
                          activeTimes={activeTimes}
                          onToggleArea={toggleArea}
                          onToggleTime={toggleTime}
                        />
                      </div>

                    </MapContainer>
                  </div>

                ) : (

                  /* ── Table View ── */
                  <div className="pipeline-section-body pipeline-section-body-ext2" style={{ padding: '0 1.25rem' }}>
                    <div className="pipeline-swarm-table-wrap">
                      {geoJsonFeeds.length === 0 ? (
                        <div className="pipeline-empty-state">No GeoJSON feeds available.</div>
                      ) : (
                        <table className="pipeline-swarm-table">
                          <thead>
                            <tr>
                              <th style={{ width: 12 }} /> {/* color swatch */}
                              <th>Area</th>
                              <th>Time</th>
                              <th>Features</th>
                              <th>Strikes (last 5)</th>
                              <th>Avg KBDI</th>
                              <th>Avg ERC</th>
                              <th style={{ width: 24 }} />
                            </tr>
                          </thead>
                          <tbody>
                            {geoJsonFeeds.map((feed) => {
                              const isExpanded = expandedFeedId === feed.id;
                              const hasError = !!feed.error;
                              const color = AREA_COLORS[feed.area] ?? '#28aee1';

                              return (
                                <React.Fragment key={feed.id}>
                                  <tr
                                    className={[
                                      'pipeline-row-clickable',
                                      hasError ? 'pipeline-row-degraded' : '',
                                      isExpanded ? 'pipeline-row-active' : '',
                                    ].join(' ')}
                                    onClick={() =>
                                      setExpandedFeedId(isExpanded ? null : feed.id)
                                    }
                                  >
                                    {/* color swatch */}
                                    <td>
                                      <span
                                        className="pipeline-geo-swatch"
                                        style={{ background: color }}
                                      />
                                    </td>
                                    <td style={{ fontWeight: 500, fontSize: '0.8rem' }}>
                                      {feed.area}
                                    </td>
                                    <td>
                                      <span className="pipeline-image-tag">{feed.time}</span>
                                    </td>
                                    <td>
                                      <span className="pipeline-replicas-tag">
                                        {feed.total_features?.toLocaleString() ?? '—'}
                                      </span>
                                    </td>
                                    <td>
                                      <span className="pipeline-replicas-tag">
                                        {feed.stats?.count ?? 0}
                                      </span>
                                    </td>
                                    <td>
                                      <span className="pipeline-image-tag">
                                        {feed.stats?.avg_kbdi?.toFixed(1) ?? '—'}
                                      </span>
                                    </td>
                                    <td>
                                      <span className="pipeline-image-tag">
                                        {feed.stats?.avg_erc?.toFixed(2) ?? '—'}
                                      </span>
                                    </td>
                                    <td>
                                      {isExpanded ? (
                                        <LuChevronDown size={14} color="var(--pipe-sub)" />
                                      ) : (
                                        <LuChevronRight size={14} color="var(--pipe-sub)" />
                                      )}
                                    </td>
                                  </tr>

                                  {/* Expanded: error state */}
                                  {isExpanded && hasError && (
                                    <tr className="pipeline-expand-row">
                                      <td colSpan={8}>
                                        <div className="pipeline-expand-content">
                                          <div
                                            className="pipeline-error-box"
                                            style={{ gridColumn: '1 / -1' }}
                                          >
                                            {feed.error}
                                            {feed.expected && (
                                              <span style={{ opacity: 0.75 }}>
                                                {' '}— expected: {feed.expected}
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                      </td>
                                    </tr>
                                  )}

                                  {/* Expanded: last5 sub-table */}
                                  {isExpanded && !hasError && (
                                    <tr className="pipeline-expand-row">
                                      <td colSpan={8}>
                                        <div className="pipeline-expand-content pipeline-expand-content-full">

                                          {/* Stats summary row */}
                                          {feed.stats && (
                                            <div className="pipeline-geo-stats-row">
                                              {[
                                                { label: 'Neg / Pos', value: `${feed.stats.negative} / ${feed.stats.positive}` },
                                                { label: 'Avg KBDI', value: feed.stats.avg_kbdi?.toFixed(1) ?? '—' },
                                                { label: 'Avg ERC', value: feed.stats.avg_erc?.toFixed(2) ?? '—' },
                                                { label: 'Avg HRL', value: feed.stats.avg_hrl?.toFixed(3) ?? '—' },
                                                { label: 'Avg Precip 24h', value: feed.stats.avg_precip24h?.toFixed(4) ?? '—' },
                                                { label: 'Avg RH Min', value: feed.stats.avg_relh_min?.toFixed(2) ?? '—' },
                                                { label: 'Avg RH Max', value: feed.stats.avg_relh_max?.toFixed(4) ?? '—' },
                                              ].map(({ label, value }) => (
                                                <div key={label} className="pipeline-geo-stat-cell">
                                                  <span className="pipeline-geo-stat-label">{label}</span>
                                                  <span className="pipeline-geo-stat-value">{value}</span>
                                                </div>
                                              ))}
                                            </div>
                                          )}

                                          {/* Last 5 mini-table */}
                                          {feed.last5 && feed.last5.length > 0 ? (
                                            <table className="pipeline-swarm-table pipeline-geo-inner-table">
                                              <thead>
                                                <tr>
                                                  <th>IDX</th>
                                                  <th>Time (UTC)</th>
                                                  <th>Lat / Lon</th>
                                                  <th>Polarity</th>
                                                  <th>KBDI</th>
                                                  <th>ERC</th>
                                                  <th>HRL</th>
                                                  <th>Precip 24h</th>
                                                </tr>
                                              </thead>
                                              <tbody>
                                                {feed.last5.map((pt, ptIdx) => (
                                                  <tr key={ptIdx}>
                                                    <td>
                                                      <span className="pipeline-solution-id">
                                                        {pt.idx}
                                                      </span>
                                                    </td>
                                                    <td>
                                                      <span className="pipeline-solution-time">
                                                        <LuClock size={10} style={{ marginRight: 3, verticalAlign: 'middle' }} />
                                                        {pt.dttime_utc}
                                                      </span>
                                                    </td>
                                                    <td>
                                                      <span className="pipeline-solution-coords">
                                                        {pt.ltg_lat.toFixed(4)}, {pt.ltg_lon.toFixed(4)}
                                                      </span>
                                                    </td>
                                                    <td>
                                                      <span
                                                        style={{
                                                          fontWeight: 600,
                                                          fontSize: '0.75rem',
                                                          color: pt.polarity < 0
                                                            ? 'var(--pipe-danger)'
                                                            : 'var(--pipe-success)',
                                                        }}
                                                      >
                                                        {pt.polarity > 0 ? '+' : ''}
                                                        {pt.polarity.toLocaleString()}
                                                      </span>
                                                    </td>
                                                    <td className="pipeline-image-tag">{pt.kbdi}</td>
                                                    <td className="pipeline-image-tag">{pt.erc}</td>
                                                    <td className="pipeline-image-tag">{pt.hrl}</td>
                                                    <td className="pipeline-image-tag">{pt.precip24h}</td>
                                                  </tr>
                                                ))}
                                              </tbody>
                                            </table>
                                          ) : (
                                            <div className="pipeline-empty-state">
                                              No strike data for this feed.
                                            </div>
                                          )}
                                        </div>
                                      </td>
                                    </tr>
                                  )}
                                </React.Fragment>
                              );
                            })}
                          </tbody>
                        </table>
                      )}
                    </div>
                  </div>
                )}
              </section>

              {/* Email */}
              <section className="pipeline-section">
                <div className="pipeline-section-header">
                  <div className='pipeline-section-title-div'>
                    <MdOutlineEmail size={16} />
                    <span className="pipeline-section-title">Today&apos;s Organization Emails</span>
                  </div>
                </div>

                <div className="pipeline-section-body" style={{ padding: '0.5rem' }}>

                  <div className="pipeline-email-table">
                    {todayEmails.length === 0 ? (
                      <div className="pipeline-empty-state">
                        No emails sent today.
                      </div>
                    ) : (
                      <table className="pipeline-swarm-table">
                        <thead>
                          <tr>
                            <th>Organization</th>
                            <th>Area</th>
                            <th style={{ width: 24 }} />
                          </tr>
                        </thead>
                        <tbody>
                          {todayEmails.map((email, i) => {
                            const emailKey = `email-${i}`;
                            const isExpanded = expandedEmailId === emailKey;
                            const succeeded = email.status === true;

                            return (
                              <React.Fragment key={emailKey}>
                                <tr
                                  className={[
                                    'pipeline-row-clickable',
                                    !succeeded ? 'pipeline-row-degraded' : '',
                                    isExpanded ? 'pipeline-row-active' : '',
                                  ].join(' ')}
                                  onClick={() => setExpandedEmailId(isExpanded ? null : emailKey)}
                                >
                                  <td style={{ fontWeight: 500, fontSize: '0.8rem' }}>
                                    {getOrgName(email.organization)}
                                  </td>
                                  <td>
                                    <span className="pipeline-image-tag">{email.area}</span>
                                  </td>
                                  <td>
                                    {isExpanded ? (
                                      <LuChevronDown size={14} color="var(--pipe-sub)" />
                                    ) : (
                                      <LuChevronRight size={14} color="var(--pipe-sub)" />
                                    )}
                                  </td>
                                </tr>

                                {isExpanded && (
                                  <tr className="pipeline-expand-row">
                                    <td colSpan={3}>
                                      <div className="pipeline-expand-content">

                                        <div className="pipeline-expand-item">
                                          <strong>Status</strong>
                                          <span
                                            className={`pipeline-status-pill ${succeeded ? 'ok' : 'degraded'}`}
                                            style={{ marginTop: '2px' }}
                                          >
                                            <span className={`pipeline-status-dot`} />
                                            {succeeded ? 'Successful' : 'Failed'}
                                          </span>
                                        </div>

                                        <div className="pipeline-expand-item pipeline-expand-item-right">
                                          <strong>Empty Email</strong>
                                          <span
                                            style={{ marginTop: '2px' }}
                                          >
                                            {email.empty_email ? 'Yes' : 'No'}
                                          </span>
                                        </div>

                                        <div className="pipeline-expand-item">
                                          <strong>Sent At</strong>
                                          <span className='pipeline-solution-time'>
                                            <LuClock size={11} style={{ marginRight: 3, verticalAlign: 'middle' }} />
                                            {fmtTimeUTC(email.email_timestamp)}
                                          </span>
                                        </div>

                                        {email.content && (
                                          <div className="pipeline-expand-item pipeline-expand-item-right">
                                            <strong>Content</strong>
                                            <span>
                                              {email.content}
                                            </span>
                                          </div>
                                        )}

                                        {!succeeded && (
                                          <div className="pipeline-error-box" style={{ gridColumn: '1 / -1' }}>
                                            Email failed to send.
                                          </div>
                                        )}

                                      </div>
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </tbody>
                      </table>
                    )}
                  </div>

                </div>
              </section>
            </div>

            {/* ── AI Second Row: Containers──────────────────── */}
            <div className="pipeline-single-row">

              {/* Container Metrics */}
              <section className="pipeline-section">
                <div className="pipeline-section-header">
                  <div className='pipeline-section-title-div'>
                    <LuContainer size={14} />
                    <span className="pipeline-section-title">Container Metrics</span>
                  </div>

                  {/* Container Health Check */}
                  <span className={`pipeline-health-badge ${containersStale ? 'degraded' : ''}`}
                    title={`Last run: ${getLastPullTime('containers')}`}>
                    <span className="pipeline-health-dot" />
                    {containersStale ? 'STALE' : 'LIVE'}
                  </span>

                </div>

                <div className="pipeline-section-body">
                  <div className="pipeline-containers-grid">
                    {sortedContainers.map((c: ContainerMetric) => {
                      const isExpanded = expandedId === c.container_id;
                      const cpuDanger = c.cpu_pct > 80;

                      return (
                        <div
                          key={c.container_id}
                          className={`pipeline-container-tile ${isExpanded ? 'pipeline-tile-expanded' : ''
                            }`}
                          onClick={() => toggleExpand(c.container_id)}
                        >
                          {/* Tile Header */}
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'flex-start',
                            }}
                          >
                            <span className="pipeline-tile-name">
                              {c.service || 'Unnamed'}
                            </span>
                            <span
                              className={`pipeline-state-dot ${c.state}`}
                              title={c.state}
                            />
                          </div>

                          {/* CPU bar */}
                          <div className="pipeline-cpu-bar-track">
                            <div
                              className={`pipeline-cpu-bar-fill ${cpuDanger ? 'pipeline-bar-danger' : ''
                                }`}
                              style={{ width: `${Math.min(c.cpu_pct, 100)}%` }}
                            />
                          </div>

                          {/* Stats */}
                          <div className="pipeline-tile-stats">
                            <div className="pipeline-tile-stat">
                              <span className="pipeline-tile-stat-label">CPU</span>
                              <span
                                className={`pipeline-tile-stat-value ${cpuDanger ? 'pipeline-stat-danger' : ''
                                  }`}
                              >
                                {c.cpu_pct.toFixed(1)}%
                              </span>
                            </div>
                            <div className="pipeline-tile-stat">
                              <span className="pipeline-tile-stat-label">RAM</span>
                              <span className="pipeline-tile-stat-value">
                                {c.mem_usage_mb.toFixed(0)} MB
                              </span>
                            </div>
                          </div>

                          {/* Expanded Detail */}
                          {isExpanded && (
                            <div className="pipeline-tile-detail">
                              <div className="pipeline-detail-field">
                                <strong>Container ID</strong>
                                <code>{c.container_id}</code>
                              </div>
                              <div className="pipeline-detail-field">
                                <strong>Image</strong>
                                <span>{c.image}</span>
                              </div>
                              <div className="pipeline-detail-field">
                                <strong>Status</strong>
                                <span>{c.status}</span>
                              </div>
                              {c.mem_limit_mb != null && (
                                <div className="pipeline-detail-field">
                                  <strong>Mem Limit</strong>
                                  <span>{c.mem_limit_mb.toFixed(0)} MB</span>
                                </div>
                              )}
                              <div className="pipeline-log-preview">
                                <pre>{c.last_log || 'No logs available.'}</pre>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </section>

            </div>

            {/* ── AI Third Row: MRMS ──────────────────── */}
            <div className="pipeline-single-row">
              <section className="pipeline-section">
                <div className="pipeline-section-header">
                  <div className="pipeline-section-title-div">
                    <TbRadar2 size={16} />
                    <span className="pipeline-section-title">MRMS Data</span>
                  </div>

                  <div className="pipeline-section-title-div" style={{ gap: '1rem' }}>

                    {/* Timestamps */}
                    {mrmsData && (
                      <div className="mrms-timestamps">
                        <span className="mrms-ts-item">
                          <span className="mrms-ts-label">Precip</span>
                          {fmtTimeUTC(mrmsData.payload.timestamps.precip)}
                        </span>
                        <span className="mrms-ts-sep">·</span>
                        <span className="mrms-ts-item">
                          <span className="mrms-ts-label">Temp</span>
                          {fmtTimeUTC(mrmsData.payload.timestamps.temp)}
                        </span>
                      </div>
                    )}

                    {/* Staleness badge */}
                    <span
                      className={`pipeline-health-badge ${mrmsStaleness ? 'degraded' : ''}`}
                      title={mrmsData ? `Last updated: ${fmt(mrmsData.last_updated)}` : 'No data'}
                    >
                      <span className="pipeline-health-dot" />
                      {mrmsStaleness ? 'STALE' : 'LIVE'}
                    </span>

                    {/* Layer toggle */}
                    <div className="mrms-layer-toggle">
                      <button
                        className={`mrms-layer-btn ${mrmsLayer === 'precip' ? 'active' : ''}`}
                        onClick={() => setMrmsLayer('precip')}
                      >
                        Precip
                      </button>
                      <button
                        className={`mrms-layer-btn ${mrmsLayer === 'temp' ? 'active' : ''}`}
                        onClick={() => setMrmsLayer('temp')}
                      >
                        Temp
                      </button>
                    </div>

                  </div>
                </div>

                {/* Map */}
                <div className="pipeline-section-body pipeline-section-body-ext pipeline-map">
                  {mrmsLoading && !mrmsData && (
                    <div className="pipeline-map-skeleton-overlay">
                      <div className="pipeline-map-skeleton-shimmer" />
                    </div>
                  )}
                  <MapContainer
                    center={mrmsMapCenter}
                    zoom={6}
                    minZoom={2}
                    style={{ width: '100%', height: '100%' }}
                  >
                    <TileLayer
                      url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    />
                    <ScaleControl position="bottomleft" metric={false} imperial={true} maxWidth={200} />

                    {mrmsData && (
                      <MrmsCanvasLayer
                        points={mrmsData.payload.points}
                        layer={mrmsLayer}
                        bounds={mrmsData.payload.meta.bounds}
                      />
                    )}

                    <div style={{ position: 'absolute', bottom: 25, right: 10, zIndex: 1000 }}>
                      <MrmsLegend layer={mrmsLayer} />
                    </div>
                  </MapContainer>
                </div>

              </section>
            </div>

          </div>
        )}

        {/* ── Inventory Section ─────────────────────────────────────── */}
        <div
          className={`pipeline-slip-header-closed slip-header ${openSlips.inventory ? 'open pipeline-slip-header' : ''
            }`}
          onClick={() => toggleSlip('inventory')}
        >
          Inventory
          <div className='slip-header-right'>
            <span className="slip-count-badge">1</span>
            <span className="slip-header-arrow">
              {openSlips.inventory ? (
                <LuChevronDown size={18} color="var(--pipe-sub)" />
              ) : (
                <LuChevronRight size={18} color="var(--pipe-sub)" />
              )}
            </span>
          </div>
        </div>

        {openSlips.inventory && (
          <div className="slip-content pipeline-slip-content">
            {/* ── Inventory First Row: Inventory ──────────────────── */}
            <div className="pipeline-single-row">

              {/* Inventory */}
              <section className="pipeline-section">
                <div className="pipeline-section-header">
                  <div className='pipeline-section-title-div'>
                    <MdOutlineInventory size={16} />
                    <span className="pipeline-section-title">Inventory</span>
                  </div>
                </div>

                <div className="pipeline-section-body">
                  <InventoryCountStatus
                    items={invInfo}
                    forecastItems={forecastInfo}
                    lateExpanded={lateExpanded}
                    setLateExpanded={setLateExpanded}
                  />
                </div>
              </section>
            </div>
          </div>
        )}


        {/* ── GIS Section ─────────────────────────────────────── */}
        <div
          className={`pipeline-slip-header-closed slip-header ${openSlips.gis ? 'open pipeline-slip-header' : ''
            }`}
          onClick={() => toggleSlip('gis')}
        >
          GIS
          <div className='slip-header-right'>
            <span className="slip-count-badge">1</span>
            <span className="slip-header-arrow">
              {openSlips.inventory ? (
                <LuChevronDown size={18} color="var(--pipe-sub)" />
              ) : (
                <LuChevronRight size={18} color="var(--pipe-sub)" />
              )}
            </span>
          </div>
        </div>

        {openSlips.gis && (
          <div className="slip-content pipeline-slip-content">
            <div className="pipeline-single-row">

              <section className="pipeline-section">
                <div className="pipeline-section-header">
                  <div className='pipeline-section-title-div'>
                    <TbAlertSquareRounded size={18} />
                    <span className="pipeline-section-title">Alerts</span>
                  </div>
                </div>

                <div className="alert-list pipeline-alert-list">

                  {/* Loading */}
                  {gisLoading && (
                    <div className="pipeline-alert-placeholder">Loading GIS alerts...</div>
                  )}

                  {/* Empty */}
                  {!gisLoading && gisAlerts.length === 0 && (
                    <div className="pipeline-alert-placeholder">No GIS alerts found.</div>
                  )}

                  {/* Alerts */}
                  {gisAlerts.map(alert => (
                    <div key={alert.id} className='alert-widget'>
                      <div className='alert-type-and-title'>
                        <div className={`pipeline-alert-type-icon ${getAlertTypeClass(alert.type?.toLowerCase())}`}>
                          {alert.type?.toLowerCase() === "info" && <IoInformationCircle className="pipeline-alert-icon" />}
                          {alert.type?.toLowerCase() === "warning" && <TiWarning className="pipeline-alert-icon" />}
                          {alert.type?.toLowerCase() === "error" && <MdOutlineError className="pipeline-alert-icon" />}
                        </div>

                        <div className='alert-title-and-date'>
                          <div className='alert-title pipeline-alert-title'>
                            <h2>{alert.title}</h2>
                            <p>Category: {alert.category}</p>
                          </div>

                          <div className='alert-date pipeline-alert-date'>
                            <span>{new Date(alert.created_at).toLocaleDateString()}</span>
                            <span className="alert-time">
                              {new Date(alert.created_at).toLocaleTimeString([], {
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </span>
                          </div>
                        </div>
                      </div>

                      <p className='pipeline-alert-body'>{alert.body}</p>
                    </div>
                  ))}

                  {/* Load More */}
                  {gisHasMore && !gisLoading && (
                    <div className="pipeline-load-more-wrapper">
                      <button
                        className="pipeline-load-more-button confirm-btn"
                        onClick={handleGISLoadMore}
                        disabled={gisLoadingMore}
                      >
                        {gisLoadingMore ? "Loading..." : "Load More"}
                      </button>
                    </div>
                  )}

                </div>

              </section>

            </div>
          </div>
        )}

      </div>

    </div>
  );
};

export default PipelineStatus;